// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import { Card } from '@mantine/core';

import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { CatalogIntrospector } from 'modules/Shop/CatalogSetup/CatalogIntrospector';
import { CatalogSelect } from 'modules/Shop/CatalogSetup/CatalogSelect';
import { CreateCatalogModal } from 'modules/Shop/CatalogSetup/CreateCatalogModal';
import { CreatePartModal } from 'modules/Shop/CatalogSetup/CreatePartModal';
import { EditCatalogPopover } from 'modules/Shop/CatalogSetup/EditCatalogPopover';
import { GlobalAttributeEditor } from 'modules/Shop/CatalogSetup/GlobalAttributeEditor';
import { PartDetail } from 'modules/Shop/CatalogSetup/PartDetail';
import { useCatalogSetup, CatalogSetupProvider } from 'modules/Shop/CatalogSetup/useCatalogSetup';

const CatalogSetupPageWrapped = () => {
  const { selectedCatalog, onCatalogSelected, editing, forceRefresh, needsRefresh, onPartSelected } = useCatalogSetup();
  const onClose = useCallback(() => forceRefresh('filters'), [forceRefresh]);
  return (
    <>
      <BasePageHeader
        title="Catalog Setup"
        topRightComponent={
          <>
            <CreateCatalogModal onCatalogCreated={onCatalogSelected} disabled={editing} />
            <CatalogSelect
              selectedCatalog={selectedCatalog}
              onCatalogSelected={onCatalogSelected}
              disabled={editing}
              w={230}
            />
            <EditCatalogPopover
              partCatalog={selectedCatalog}
              onCatalogUpdated={onCatalogSelected}
              onCatalogDeleted={() => onCatalogSelected(null)}
            />
            <GlobalAttributeEditor disabled={editing || !selectedCatalog?.editable} onClose={onClose} />
          </>
        }
      />
      <Card withBorder p={0} flex="1" display="flex" style={{ overflowX: 'auto' }}>
        <CatalogIntrospector
          partCatalog={selectedCatalog}
          onPartSelected={onPartSelected}
          rightSideComponent={({ partCatalog, categoryPath, selectedCategory, onPartSelected: onPartSelectedInner }) =>
            partCatalog.editable && (
              <CreatePartModal
                partCatalog={partCatalog}
                categoryPath={categoryPath}
                onPartCreated={(part) => {
                  forceRefresh('filters', 'categories');
                  onPartSelectedInner(part);
                  if (part.partCategoryId === selectedCategory?.partCategoryId) {
                    forceRefresh('parts');
                  }
                }}
              />
            )
          }
          rightPane={({ partCatalog, selectedCategory, onPartSelected: onPartSelectedInner }) => (
            <PartDetail
              partCatalog={partCatalog}
              partCategory={selectedCategory}
              onPartSelected={onPartSelectedInner}
            />
          )}
          rightPaneSizeArgs={{
            defaultSize: 320,
            min: 320,
            max: 450,
          }}
          disabled={editing}
          allowEditing
          needsRefresh={needsRefresh}
          useQueryParams
        />
      </Card>
    </>
  );
};

export const CatalogSetupPage = () => (
  <CatalogSetupProvider>
    <CatalogSetupPageWrapped />
  </CatalogSetupProvider>
);
